/**
 * newsletter.js — "Letters from Japan" subscribe form handler.
 * ──────────────────────────────────────────────────────────────────────
 * Responsibilities
 *   - Email validation (HTML5 + lightweight regex sanity check)
 *   - Inline status messages via [data-newsletter-status] (aria-live polite)
 *   - Disable submit while in-flight, restore after
 *   - Simulate API success after 600ms (no real backend wired yet)
 *
 * Markup contract (see public/_partials/footer.html)
 *   <form data-newsletter-form>
 *     <input type="email" data-newsletter-input required>
 *     <button data-newsletter-submit>Subscribe</button>
 *     <p data-newsletter-status role="status" aria-live="polite"></p>
 *   </form>
 *
 * Auto-init: on DOMContentLoaded, every matching form is bound.
 * Manual init: import { initNewsletter } from './newsletter.js'; initNewsletter(rootEl?);
 *
 * TODO: hook to real subscribe endpoint
 *       - Replace `simulateSubscribe()` with a fetch() POST to the Mailchimp/Klaviyo
 *         endpoint chosen for Phase 1.
 *       - Honor duplicate-email response (per 機能要件定義書 §4.2.4 — show error msg).
 */

const SELECTORS = Object.freeze({
  form:   '[data-newsletter-form]',
  input:  '[data-newsletter-input]',
  submit: '[data-newsletter-submit]',
  status: '[data-newsletter-status]',
});

const MESSAGES = Object.freeze({
  success: 'Thank you  /  ありがとうございます',
  invalid: 'Please enter a valid email.',
  network: 'Sorry, something went wrong. Please try again.',
});

const STATE = Object.freeze({
  idle:    'idle',
  loading: 'loading',
  success: 'success',
  error:   'error',
});

/* RFC-5322 light: enforces local@domain.tld with no whitespace.
 * Intentionally permissive — server is the source of truth. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validate an email string.
 * @param {string} value
 * @returns {boolean}
 */
function isValidEmail(value) {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > 254) return false;
  return EMAIL_RE.test(trimmed);
}

/**
 * Write a status message into the form's [data-newsletter-status] node.
 * @param {HTMLElement} statusEl
 * @param {string} text
 * @param {keyof typeof STATE} state
 */
function setStatus(statusEl, text, state) {
  if (!statusEl) return;
  statusEl.textContent = text;
  if (state === STATE.idle) {
    statusEl.removeAttribute('data-state');
  } else {
    statusEl.setAttribute('data-state', state);
  }
}

/**
 * Toggle the submit button busy state.
 * @param {HTMLButtonElement} btn
 * @param {boolean} busy
 */
function setBusy(btn, busy) {
  if (!btn) return;
  btn.disabled = busy;
  if (busy) {
    btn.setAttribute('aria-busy', 'true');
  } else {
    btn.removeAttribute('aria-busy');
  }
}

/**
 * Simulate a subscribe API call.
 * Resolves after 600ms with { ok: true }.
 * Replace with real fetch() once the endpoint is decided.
 * @param {string} email
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
function simulateSubscribe(email) {
  return new Promise((resolve) => {
    // TODO: hook to real subscribe endpoint
    setTimeout(() => resolve({ ok: true, email }), 600);
  });
}

/**
 * Bind a single newsletter form.
 * Idempotent — calling twice on the same form is a no-op.
 * @param {HTMLFormElement} form
 */
function bindForm(form) {
  if (!(form instanceof HTMLFormElement)) return;
  if (form.dataset.newsletterBound === 'true') return;
  form.dataset.newsletterBound = 'true';

  const input    = form.querySelector(SELECTORS.input);
  const submit   = form.querySelector(SELECTORS.submit);
  const statusEl = form.querySelector(SELECTORS.status);

  if (!input || !submit) {
    // Markup contract violated — fail loud in dev, silent in prod.
    if (typeof console !== 'undefined') {
      console.warn('[newsletter] missing required child elements', form);
    }
    return;
  }

  /* Clear stale error styling once user starts editing again. */
  input.addEventListener('input', () => {
    if (statusEl && statusEl.dataset.state === STATE.error) {
      setStatus(statusEl, '', STATE.idle);
    }
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const email = input.value;

    if (!isValidEmail(email)) {
      setStatus(statusEl, MESSAGES.invalid, STATE.error);
      input.focus();
      input.setAttribute('aria-invalid', 'true');
      return;
    }
    input.removeAttribute('aria-invalid');

    setBusy(submit, true);
    setStatus(statusEl, '', STATE.loading);

    try {
      const result = await simulateSubscribe(email);
      if (result && result.ok) {
        setStatus(statusEl, MESSAGES.success, STATE.success);
        form.reset();
      } else {
        setStatus(statusEl, MESSAGES.network, STATE.error);
      }
    } catch (err) {
      setStatus(statusEl, MESSAGES.network, STATE.error);
      if (typeof console !== 'undefined') {
        console.error('[newsletter] subscribe failed', err);
      }
    } finally {
      setBusy(submit, false);
    }
  });
}

/**
 * Initialize all newsletter forms inside `root` (defaults to document).
 * @param {ParentNode} [root=document]
 * @returns {number} count of forms bound
 */
function initNewsletter(root = document) {
  const forms = root.querySelectorAll(SELECTORS.form);
  forms.forEach(bindForm);
  return forms.length;
}

/* Auto-bind on DOM ready (defer-loaded modules → run immediately). */
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initNewsletter(), { once: true });
  } else {
    initNewsletter();
  }
}

/* Named exports for tests / advanced consumers. */
{ isValidEmail, MESSAGES, STATE, SELECTORS };


/* expose globally + auto-init (replaces removed export) */
(function(){
  try { window.KoshoNewsletter = { init: initNewsletter }; } catch(e) {}
  if (document.readyState !== 'loading') { try { initNewsletter(); } catch(e) {} }
  else { document.addEventListener('DOMContentLoaded', function(){ try { initNewsletter(); } catch(e) {} }); }
})();
